import { ActionStatusHelper } from "@/Shared/Helpers";
import { useEffect, useState } from "react";
import { DOMAIN_CONFIGS, DomainHookProps, useAppNavigation, useDeleteDomain, useRestoreDomain } from "@/Shared";
import { ActionResponse, DomainType, Entity, EntityHook } from "@/Shared/Types";

/**
 * useDetailLifeCycle hook
 *
 * Generic lifecycle orchestration hook for domain entities.
 *
 * This hook standardizes the full detail or form workflow for any domain,
 * including:
 * - Entity initialization (create / edit mode)
 * - Data fetching and resolution
 * - ViewModel construction
 * - Delete / restore workflow
 * - Post-success extension logic
 * - Computation of the unified response
 *
 * Responsibilities:
 * - Resolve current entity (queryId / entityId logic)
 * - Guard initialization based on combined response status
 * - Build and expose the ViewModel to the UI layer
 * - Handle delete and restore actions with confirmation and feedback
 * - Protect against state updates after unmount
 *
 * Author: Samano CASTRE
 * Date: March 2, 2026
 */
export const useDetailLifeCycle = <ViewModel, Model extends Entity>(
    config: {
        response: ActionResponse,
        domainType: DomainType,
        props: DomainHookProps,
        entityHook: EntityHook<Model>,
        buildVM: (currentEntity: Model, isDisplaying: boolean) => ViewModel,
        onSuccess?: () => Promise<void>,
        deps?: (string | boolean | number)[]
    }
) => {

    const {
        response,
        domainType,
        props,
        entityHook,
        buildVM,
        onSuccess,
        deps
    } = config;

    const { extraArgs, inModalHook, accessRight, uiFeedbackHooks } = props;
    const { entityId, entity } = extraArgs ?? {};
    const inModal = !!inModalHook;
    const { domainLabel } = DOMAIN_CONFIGS[domainType];

    /**
     * Navigation helpers for layout transitions
     * and error page redirections
     */
    const { goToLayout } = useAppNavigation();

    /**
     * Entity Hook Operations
     *
     * Provides:
     * - queryId resolution
     * - dataMap access
     * - current entity fetching
     * - CRUD actions
     */
    const {
        queryId,
        dataMap,
        findCurrentAction,
        deleteAction,
        restoreAction,
    } = entityHook;


    /**
     * ViewModel State
     *
     * Holds the constructed ViewModel
     * exposed to the UI layer.
     */
    const [entityVM, setEntityVM] = useState<ViewModel>();


    /**
     * Request Identifier
     *
     * Unified identifier used by the UI
     * to determine create or edit mode.
     */
    const requestId = entityId ?? entity?.id ?? queryId;

    const handleSuccess = () => {
        if(onSuccess) {
            return onSuccess;
        }
        if(inModal) {
            return inModalHook?.closeModal
        }
        return (() => goToLayout(domainType));
    }

     /**
     * Delete workflow delegated to a generic hook
     *
     * Encapsulates confirmation, toast feedback, and API call.
     * Optionally calls post-success logic.
     */
    const { executeDelete } = useDeleteDomain({
        domainLabel,
        canDelete: accessRight.canDelete(domainType),
        deleteAction,
        uiFeedbackHooks,
        onSuccess: handleSuccess()
    });


    /**
     * Restore workflow delegated to a generic hook
     *
     * Encapsulates confirmation, toast feedback, and API call.
     * Optionally calls post-success logic.
     */
    const { executeRestore } = useRestoreDomain({
        domainLabel,
        canRestore: accessRight.canRestore(domainType),
        restoreAction,
        uiFeedbackHooks,
        onSuccess: handleSuccess()
    });


    /**
     * ViewModel Initialization Effect
     *
     * Triggers when:
     * - The combined response is successful
     * - Entity identifiers change
     *
     * Behavior:
     * - Resolves current entity (edit mode only)
     * - Builds the ViewModel
     * - Prevents state update after unmount
     */
    useEffect(() => {

        if (!ActionStatusHelper.isSucceeded(response.status) || !requestId) {
            return;
        }
        let cancelled = false;
        const init = async () => {
            const currentEntity = entity ?? await findCurrentAction.execute();
            if (cancelled || !currentEntity) {
                return;
            }

            const isDisplaying = currentEntity.id === Number(requestId);
            setEntityVM(
                buildVM(
                    currentEntity, 
                    isDisplaying
                )
            )
        }
        init();
        return () => {
            cancelled = true;
        };
    }, [
        response.status,
        requestId,
        domainType,
        ...(deps ?? [])
    ])

    /**
     * Public API
     *
     * Exposes only the data and actions required
     * by the UI layer.
     */
    return {
        // Form
        entityVM,
        response: findCurrentAction.response.error ? findCurrentAction.response : response,
        accessRight,
        requestId,
        executeDelete,
        executeRestore,
        deleteStatus: deleteAction.response?.status,
        restoreStatus: restoreAction.response?.status,
    }
}